import {
	Outlet,
	createRootRoute,
	HeadContent,
	Scripts,
} from "@tanstack/react-router";
import { ClerkProvider } from "@clerk/tanstack-react-start";
import { auth } from "@clerk/tanstack-react-start/server";
import { createClerkClient } from "@clerk/backend";
import { createServerFn } from "@tanstack/react-start";
import type { ReactNode } from "react";
import globalsCss from "../globals.css?url";
import { Navbar } from "~/components/navbar";
import { Footer } from "~/components/footer";
import { FloatingHexagons } from "~/components/floating-hexagons";

const clerkClient = createClerkClient({
	secretKey: process.env.CLERK_SECRET_KEY,
});

export type NavbarViewer = {
	fullName: string | null;
	imageUrl: string | null;
} | null;

const getNavbarViewer = createServerFn({ method: "GET" }).handler(
	async (): Promise<NavbarViewer> => {
		const { isAuthenticated, userId } = await auth();

		if (!isAuthenticated || !userId) return null;

		const user = await clerkClient.users.getUser(userId);

		return {
			fullName: user.fullName ?? user.firstName ?? null,
			imageUrl: user.imageUrl ?? null,
		};
	},
);

export function LoadingSpinner() {
	return (
		<div className="flex min-h-[calc(100vh-4rem)] items-center justify-center">
			<div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
		</div>
	);
}

export const Route = createRootRoute({
	staleTime: 60_000,
	beforeLoad: async () => ({
		navbarViewer: await getNavbarViewer(),
	}),
	head: () => ({
		meta: [
			{ charSet: "utf-8" },
			{
				name: "viewport",
				content: "width=device-width, initial-scale=1",
			},
			{ title: "Overseer — ARK Server Management for Discord" },
			{
				name: "description",
				content:
					"Powerful Discord bot to manage your ARK: Survival Evolved servers on Nitrado. Ban players, monitor logs, track stats, and more.",
			},
		],
		links: [
			{ rel: "stylesheet", href: globalsCss },
			{ rel: "icon", href: "/icon-256.png", type: "image/png" },
			{ rel: "apple-touch-icon", href: "/icon-512.png" },
		],
	}),
	component: RootComponent,
});

function RootComponent() {
	const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
	const { navbarViewer } = Route.useRouteContext();

	if (!publishableKey) {
		throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY");
	}

	return (
		<ClerkProvider publishableKey={publishableKey}>
			<ClerkGate navbarViewer={navbarViewer} />
		</ClerkProvider>
	);
}

function ClerkGate({ navbarViewer }: { navbarViewer: NavbarViewer }) {
	return (
		<RootDocument>
			<FloatingHexagons />
			<Navbar initialViewer={navbarViewer} />
			<div className="relative z-10 mt-16 flex min-h-[calc(100vh-4rem)] flex-col">
				<main className="flex-1">
					<Outlet />
				</main>
				<Footer />
			</div>
		</RootDocument>
	);
}

function RootDocument({ children }: { children: ReactNode }) {
	return (
		<html lang="en">
			<head>
				<HeadContent />
			</head>
			<body>
				{children}
				<Scripts />
			</body>
		</html>
	);
}
